import React, { useCallback, useEffect, useState } from "react";
import styles from "./StoreFrontItem.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import ProductImagesSwiper from "../components/product-images-swiper/ProductImagesSwiper";
import ProductItem from "../components/product-item/ProductItem";
import PaymentPlanSideDialog from "./payment-plan-side-dialog/PaymentPlanSideDialog";
import AddOnForm from "./add-on-form/AddOnForm";
import { useLazyViewProductQuery } from "../../../service/solar-product-service";
import { AvailableOffer, SolarProductObject } from "../../../model/solar-product/solar-product-with-recommendations";
import { toast } from "react-toastify";
import CommonLoadingSpinnerForPage from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { AppDispatch } from "../../../stores/store";
import { useDispatch } from "react-redux";
import { newBuySolarProductDetails } from "../../../stores/slices/buy-solar-product-slice";
import { BuildingType } from "../../../model/building-type";
import { PaymentMethod } from "../../../model/payment-method";
import { ApplicationRoutes } from "../../../routes";
import { useForm } from "react-hook-form";
import { RoofType } from "../../../model/solar-recommendation-form/roofType";

type Props = {};

enum PaymentOption {
	Installmental,
	OneTime,
}

export enum AddOnOption {
	Installation,
	Insurance,
}

const StoreFrontItem: React.FC<Props> = (props: Props) => {
	const { id } = useParams();
	const [solarProduct, setSolarProduct] = useState<SolarProductObject>();
	const [recommendedProducts, setRecommendedProducts] = useState<SolarProductObject[]>([]);
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		formState: { errors, isValid, isSubmitted },
	} = useForm<{
		selectedPayment?: PaymentMethod;
		selectedPlan?: AvailableOffer;
		addOns: AddOnOption[];
		roofType?: RoofType;
		floorsNumber: number;
	}>({
		defaultValues: {
			selectedPayment: undefined,
			roofType: undefined,
			selectedPlan: undefined,
			addOns: [],
			floorsNumber: 1,
		},
		reValidateMode: "onSubmit",
		mode: "onBlur",
	});

	register("selectedPlan", { required: watch("selectedPayment") === "Installments" ? "Please select a Payment plan" : false });

	const dispatch = useDispatch<AppDispatch>();

	const navigate = useNavigate();

	const isInstallmentalSelected = watch("selectedPayment") === "Installments";
	const isOneTimeSelected = watch("selectedPayment") === "One Time Payment";
	const isPlanSelected = watch("selectedPlan") !== null && watch("selectedPlan") !== undefined;
	const selectedPlan = watch("selectedPlan");

	const openDialog = (): void => {
		setIsDialogOpen(true);
	};

	const closeDialog = (): void => {
		setIsDialogOpen(false);
	};

	const handleSelectPlan = (plan: AvailableOffer): void => {
		setValue("selectedPlan", plan, { shouldValidate: true });
		closeDialog();
	};

	const onBuyNow = (): void => {
		if (!isValid) {
			toast.error(errors.selectedPlan?.message ?? "Please select a Payment plan");
			return;
		}

		dispatch(
			newBuySolarProductDetails({
				durationOfPaymentsInMonths: watch("selectedPlan")?.months,
				numberOfFloors: watch("floorsNumber"),
				paymentMethod: watch("selectedPayment"),
				productDetailsId: solarProduct?.solarProduct.id!,
				roofTypeId: watch("roofType")?.id!,
			})
		);

		navigate(ApplicationRoutes.CONFIRM_ORDER);
	};

	const [getProduct, { isLoading }] = useLazyViewProductQuery();

	const fetchSolarProductDetails = useCallback(async () => {
		try {
			if (id != null) {
				let response = await getProduct(id, true).unwrap();

				setSolarProduct({
					solarProduct: {
						...response.solarProduct.solarProduct,
					},
					availableOffers: [...response.solarProduct.availableOffers],
				});

				setRecommendedProducts(
					response.recommendedProducts.map((product: SolarProductObject) => ({
						solarProduct: {
							...product.solarProduct,
						},
						availableOffers: [...product.availableOffers],
					}))
				);
			}
		} catch (error) {
			toast.error("Error loading Product Details");
		}
	}, [getProduct, id]);

	const handleWishlistChange = (productId: string, isInWishlist: boolean) => {
		setRecommendedProducts(prevCategoryWithSolarProducts => {
			if (!prevCategoryWithSolarProducts) return prevCategoryWithSolarProducts;

			return {
				...prevCategoryWithSolarProducts,
				productsAndAvailableOffers: prevCategoryWithSolarProducts?.map(product =>
					product.solarProduct.id === productId
						? {
								...product,
								solarProduct: {
									...product.solarProduct,
									isWishlisted: isInWishlist,
								},
						  }
						: product
				),
			};
		});
	};

	useEffect(() => {
		fetchSolarProductDetails();
	}, [fetchSolarProductDetails]);

	return (
		<CommonLoadingSpinnerForPage loading={isLoading}>
			<div className={styles.container}>
				<div className={styles.banner}></div>
				<div className={styles.content}>
					<div className={styles.productOverview}>
						<div className={styles.general}>
							<div className={styles.imagesContainer}>
								<ProductImagesSwiper images={solarProduct?.solarProduct.images} />
								<div className={`${styles.info} ${styles.showAbove992}`}>
									<div className={styles.infoItem}>
										<div className={styles.itemIcon}>
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path
													d="M8 4.66892V8L10.1554 10.1554M15.25 8C15.25 12.004 12.004 15.25 8 15.25C3.99594 15.25 0.75 12.004 0.75 8C0.75 3.99594 3.99594 0.75 8 0.75C12.004 0.75 15.25 3.99594 15.25 8Z"
													stroke="black"
													stroke-width="1.5"
													stroke-linecap="round"
													stroke-linejoin="round"
												/>
											</svg>
										</div>
										Up to {solarProduct?.solarProduct.activeDuration} HOURS
									</div>
									<div className={styles.infoItem}>
										<div className={styles.itemIcon}>
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path
													d="M14.75 4.52273V2.88636C14.75 1.98262 14.0174 1.25 13.1136 1.25H2.88636C1.98262 1.25 1.25 1.98262 1.25 2.88636V4.52273M14.75 4.52273V13.1136C14.75 14.0174 14.0174 14.75 13.1136 14.75H2.88636C1.98262 14.75 1.25 14.0174 1.25 13.1136V4.52273M14.75 4.52273H1.25M4.72727 8V7.99182M8 8V7.99182M4.72727 11.2727V11.2645M8 11.2727V11.2645M11.2727 8V7.99182M4.93182 8C4.93182 8.11299 4.84024 8.20455 4.72727 8.20455C4.61431 8.20455 4.52273 8.11299 4.52273 8C4.52273 7.88701 4.61431 7.79545 4.72727 7.79545C4.84024 7.79545 4.93182 7.88701 4.93182 8ZM8.20455 8C8.20455 8.11299 8.11299 8.20455 8 8.20455C7.88701 8.20455 7.79545 8.11299 7.79545 8C7.79545 7.88701 7.88701 7.79545 8 7.79545C8.11299 7.79545 8.20455 7.88701 8.20455 8ZM4.93182 11.2727C4.93182 11.3857 4.84024 11.4773 4.72727 11.4773C4.61431 11.4773 4.52273 11.3857 4.52273 11.2727C4.52273 11.1597 4.61431 11.0682 4.72727 11.0682C4.84024 11.0682 4.93182 11.1597 4.93182 11.2727ZM8.20455 11.2727C8.20455 11.3857 8.11299 11.4773 8 11.4773C7.88701 11.4773 7.79545 11.3857 7.79545 11.2727C7.79545 11.1597 7.88701 11.0682 8 11.0682C8.11299 11.0682 8.20455 11.1597 8.20455 11.2727ZM11.4773 8C11.4773 8.11299 11.3857 8.20455 11.2727 8.20455C11.1597 8.20455 11.0682 8.11299 11.0682 8C11.0682 7.88701 11.1597 7.79545 11.2727 7.79545C11.3857 7.79545 11.4773 7.88701 11.4773 8Z"
													stroke="black"
													stroke-width="1.5"
													stroke-linecap="round"
												/>
											</svg>
										</div>
										Up to a {solarProduct?.availableOffers.sort((a, b) => b.months - a.months)[0].months} Months Plan
									</div>
									<div className={styles.infoItem}>
										<div className={styles.itemIcon}>
											<svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path
													d="M4.27713 4.18243H2.60146C1.67601 4.18243 0.925781 4.93266 0.925781 5.85811V10.8851C0.925781 12.736 2.42623 14.2365 4.27713 14.2365H7.00011M4.27713 4.18243V1.25M4.27713 4.18243H9.72308M7.00011 14.2365H9.72308C11.5739 14.2365 13.0744 12.736 13.0744 10.8851V5.85811C13.0744 4.93266 12.3242 4.18243 11.3988 4.18243H9.72308M7.00011 14.2365V16.75M9.72308 4.18243V1.25"
													stroke="black"
													stroke-width="1.5"
													stroke-linecap="round"
													stroke-linejoin="round"
												/>
											</svg>
										</div>
										{solarProduct?.solarProduct.productCapacity} KVA
									</div>
								</div>
							</div>
							<div className={styles.productDetails}>
								<div className={styles.ratings}>
									{solarProduct?.solarProduct.hotDeal && <div className={styles.hotSaleMark}>HOT SALE</div>}
									{/* <div className={styles.score}>
                                        <div className={styles.stars}>
                                            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.77752 12.5742L12.0524 14.6535C12.4748 14.9199 12.9946 14.5235 12.8711 14.0362L11.9225 10.3065C11.8968 10.2031 11.9009 10.0946 11.9342 9.99339C11.9676 9.89223 12.0288 9.80254 12.1109 9.73466L15.0479 7.28499C15.4313 6.9666 15.2363 6.32331 14.736 6.29082L10.9023 6.04391C10.7977 6.03782 10.6971 6.0014 10.6129 5.93908C10.5287 5.87677 10.4644 5.79127 10.428 5.69303L8.99845 2.09324C8.96061 1.98921 8.89168 1.89935 8.801 1.83585C8.71033 1.77234 8.60232 1.73828 8.49162 1.73828C8.38093 1.73828 8.27291 1.77234 8.18224 1.83585C8.09157 1.89935 8.02263 1.98921 7.98479 2.09324L6.55528 5.69303C6.51884 5.79127 6.45458 5.87677 6.37034 5.93908C6.2861 6.0014 6.18554 6.03782 6.08094 6.04391L2.24723 6.29082C1.7469 6.32331 1.55196 6.9666 1.93534 7.28499L4.87234 9.73466C4.95443 9.80254 5.01568 9.89223 5.04902 9.99339C5.08235 10.0946 5.08643 10.2031 5.06078 10.3065L4.18358 13.7633C4.03413 14.3481 4.65792 14.8224 5.15825 14.5041L8.20572 12.5742C8.29118 12.5199 8.39035 12.491 8.49162 12.491C8.59289 12.491 8.69207 12.5199 8.77752 12.5742V12.5742Z" fill="#F0B005"/>
                                            </svg>
                                            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.77752 12.5742L12.0524 14.6535C12.4748 14.9199 12.9946 14.5235 12.8711 14.0362L11.9225 10.3065C11.8968 10.2031 11.9009 10.0946 11.9342 9.99339C11.9676 9.89223 12.0288 9.80254 12.1109 9.73466L15.0479 7.28499C15.4313 6.9666 15.2363 6.32331 14.736 6.29082L10.9023 6.04391C10.7977 6.03782 10.6971 6.0014 10.6129 5.93908C10.5287 5.87677 10.4644 5.79127 10.428 5.69303L8.99845 2.09324C8.96061 1.98921 8.89168 1.89935 8.801 1.83585C8.71033 1.77234 8.60232 1.73828 8.49162 1.73828C8.38093 1.73828 8.27291 1.77234 8.18224 1.83585C8.09157 1.89935 8.02263 1.98921 7.98479 2.09324L6.55528 5.69303C6.51884 5.79127 6.45458 5.87677 6.37034 5.93908C6.2861 6.0014 6.18554 6.03782 6.08094 6.04391L2.24723 6.29082C1.7469 6.32331 1.55196 6.9666 1.93534 7.28499L4.87234 9.73466C4.95443 9.80254 5.01568 9.89223 5.04902 9.99339C5.08235 10.0946 5.08643 10.2031 5.06078 10.3065L4.18358 13.7633C4.03413 14.3481 4.65792 14.8224 5.15825 14.5041L8.20572 12.5742C8.29118 12.5199 8.39035 12.491 8.49162 12.491C8.59289 12.491 8.69207 12.5199 8.77752 12.5742V12.5742Z" fill="#F0B005"/>
                                            </svg>
                                            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.77752 12.5742L12.0524 14.6535C12.4748 14.9199 12.9946 14.5235 12.8711 14.0362L11.9225 10.3065C11.8968 10.2031 11.9009 10.0946 11.9342 9.99339C11.9676 9.89223 12.0288 9.80254 12.1109 9.73466L15.0479 7.28499C15.4313 6.9666 15.2363 6.32331 14.736 6.29082L10.9023 6.04391C10.7977 6.03782 10.6971 6.0014 10.6129 5.93908C10.5287 5.87677 10.4644 5.79127 10.428 5.69303L8.99845 2.09324C8.96061 1.98921 8.89168 1.89935 8.801 1.83585C8.71033 1.77234 8.60232 1.73828 8.49162 1.73828C8.38093 1.73828 8.27291 1.77234 8.18224 1.83585C8.09157 1.89935 8.02263 1.98921 7.98479 2.09324L6.55528 5.69303C6.51884 5.79127 6.45458 5.87677 6.37034 5.93908C6.2861 6.0014 6.18554 6.03782 6.08094 6.04391L2.24723 6.29082C1.7469 6.32331 1.55196 6.9666 1.93534 7.28499L4.87234 9.73466C4.95443 9.80254 5.01568 9.89223 5.04902 9.99339C5.08235 10.0946 5.08643 10.2031 5.06078 10.3065L4.18358 13.7633C4.03413 14.3481 4.65792 14.8224 5.15825 14.5041L8.20572 12.5742C8.29118 12.5199 8.39035 12.491 8.49162 12.491C8.59289 12.491 8.69207 12.5199 8.77752 12.5742V12.5742Z" fill="#F0B005"/>
                                            </svg>
                                            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.77752 12.5742L12.0524 14.6535C12.4748 14.9199 12.9946 14.5235 12.8711 14.0362L11.9225 10.3065C11.8968 10.2031 11.9009 10.0946 11.9342 9.99339C11.9676 9.89223 12.0288 9.80254 12.1109 9.73466L15.0479 7.28499C15.4313 6.9666 15.2363 6.32331 14.736 6.29082L10.9023 6.04391C10.7977 6.03782 10.6971 6.0014 10.6129 5.93908C10.5287 5.87677 10.4644 5.79127 10.428 5.69303L8.99845 2.09324C8.96061 1.98921 8.89168 1.89935 8.801 1.83585C8.71033 1.77234 8.60232 1.73828 8.49162 1.73828C8.38093 1.73828 8.27291 1.77234 8.18224 1.83585C8.09157 1.89935 8.02263 1.98921 7.98479 2.09324L6.55528 5.69303C6.51884 5.79127 6.45458 5.87677 6.37034 5.93908C6.2861 6.0014 6.18554 6.03782 6.08094 6.04391L2.24723 6.29082C1.7469 6.32331 1.55196 6.9666 1.93534 7.28499L4.87234 9.73466C4.95443 9.80254 5.01568 9.89223 5.04902 9.99339C5.08235 10.0946 5.08643 10.2031 5.06078 10.3065L4.18358 13.7633C4.03413 14.3481 4.65792 14.8224 5.15825 14.5041L8.20572 12.5742C8.29118 12.5199 8.39035 12.491 8.49162 12.491C8.59289 12.491 8.69207 12.5199 8.77752 12.5742V12.5742Z" fill="#F0B005"/>
                                            </svg>
                                            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.96893 12.5742L12.2438 14.6535C12.6662 14.9199 13.186 14.5235 13.0625 14.0362L12.1139 10.3065C12.0882 10.2031 12.0923 10.0946 12.1256 9.99339C12.159 9.89223 12.2202 9.80254 12.3023 9.73466L15.2393 7.28499C15.6227 6.9666 15.4278 6.32331 14.9274 6.29082L11.0937 6.04391C10.9891 6.03782 10.8885 6.0014 10.8043 5.93908C10.7201 5.87677 10.6558 5.79127 10.6194 5.69303L9.18986 2.09324C9.15202 1.98921 9.08308 1.89935 8.99241 1.83585C8.90174 1.77234 8.79372 1.73828 8.68303 1.73828C8.57233 1.73828 8.46432 1.77234 8.37364 1.83585C8.28297 1.89935 8.21404 1.98921 8.1762 2.09324L6.74668 5.69303C6.71025 5.79127 6.64599 5.87677 6.56175 5.93908C6.47751 6.0014 6.37695 6.03782 6.27234 6.04391L2.43864 6.29082C1.93831 6.32331 1.74337 6.9666 2.12674 7.28499L5.06375 9.73466C5.14584 9.80254 5.20708 9.89223 5.24042 9.99339C5.27376 10.0946 5.27784 10.2031 5.25219 10.3065L4.37498 13.7633C4.22553 14.3481 4.84932 14.8224 5.34965 14.5041L8.39712 12.5742C8.48258 12.5199 8.58176 12.491 8.68303 12.491C8.7843 12.491 8.88347 12.5199 8.96893 12.5742V12.5742Z" fill="black" fill-opacity="0.15"/>
                                            </svg>
                                        </div>
                                        4.7
                                    </div> */}
								</div>
								<div className={styles.name}>
									{solarProduct?.solarProduct.productName}
									<div className={styles.details}>
										{/* <div className={styles.detail}>
                                            Brand&nbsp;&nbsp;
                                            <span className={styles.detailValue}>Eco Gravity</span>
                                        </div> */}
										<div className={styles.detail}>
											Availability&nbsp;&nbsp;
											{solarProduct?.solarProduct.quantityAvailableForSale != null && solarProduct?.solarProduct.quantityAvailableForSale > 0 ? (
												<span className={`${styles.detailValue} ${styles.green}`}>In Stock</span>
											) : (
												<span className={`${styles.detailValue} ${styles.red}`}>Not Available</span>
											)}
										</div>
									</div>
								</div>
								<div className={styles.price}>
									<span className={styles.from}>From</span>
									<span className={styles.amount}>
										₦
										{(
											solarProduct?.solarProduct.price! * 0.3 +
											solarProduct?.availableOffers.sort((a, b) => a.months - b.months)[0].price! *
												solarProduct?.availableOffers.sort((a, b) => a.months - b.months)[0].months!
										).toLocaleString()}
									</span>
								</div>
								<div className={styles.payment}>
									How would you like to pay?
									<div className={styles.paymentOptions}>
										<div
											className={`${styles.paymentOption} ${isInstallmentalSelected ? styles.selected : ""}`}
											onClick={() =>
												setValue("selectedPayment", "Installments", {
													shouldValidate: true,
												})
											}
										>
											<div className={styles.paymentDetails}>
												<div className={styles.paymentOptionHeader}>
													Installmental
													{isInstallmentalSelected && (
														<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path
																fillRule="evenodd"
																clipRule="evenodd"
																d="M12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2ZM16.0805 9.97493C16.3428 9.65434 16.2955 9.18183 15.9749 8.91953C15.6543 8.65724 15.1818 8.70449 14.9195 9.02507L10.9443 13.8837L9.53033 12.4697C9.23744 12.1768 8.76256 12.1768 8.46967 12.4697C8.17678 12.7626 8.17678 13.2374 8.46967 13.5303L10.4697 15.5303C10.6195 15.6802 10.8257 15.7596 11.0374 15.7491C11.249 15.7385 11.4463 15.6389 11.5805 15.4749L16.0805 9.97493Z"
																fill="#FF7926"
															/>
														</svg>
													)}
												</div>
												{isInstallmentalSelected && (
													<div className={styles.paymentOptionBody}>
														<div className={styles.paymentDetail}>
															<div className={styles.paymentDetailName}>Initial Deposit</div>
															<div className={styles.paymentDetailValue}>30%</div>
														</div>
														<div className={styles.paymentDetail}>
															<div className={styles.paymentDetailName}>Highest possible tenure</div>
															<div className={styles.paymentDetailValue}>
																{solarProduct?.availableOffers.sort((a, b) => b.months - a.months)[0].months} Months
															</div>
														</div>
														<div className={styles.paymentDetail}>
															<div className={styles.paymentDetailName}>Full price as of today</div>
															<div className={styles.paymentDetailValue}>
																₦
																{(
																	solarProduct?.solarProduct.price! * 0.3 +
																	solarProduct?.availableOffers.sort((a, b) => a.months - b.months)[0].price! *
																		solarProduct?.availableOffers.sort((a, b) => a.months - b.months)[0].months!
																).toLocaleString()}
															</div>
														</div>
													</div>
												)}
											</div>
											{isInstallmentalSelected && (
												<div className={styles.paymentForm}>
													<div className={styles.paymentPlan}>
														Choose Payment Plan
														<div className={`${styles.paymentPlanSelect} ${isPlanSelected ? styles.planSelected : ""}`} onClick={openDialog}>
															{isPlanSelected ? `${selectedPlan!.months} months` : "Select Plan"}
														</div>
													</div>
													<AddOnForm
														solarProduct={solarProduct}
														selectedAddOns={watch("addOns")}
														setSelectedAddOn={addOns => {
															setValue("addOns", addOns);
														}}
														selectedRoofType={watch("roofType")}
														setSelectedRoofType={roofType => {
															setValue("roofType", roofType);
														}}
														floorsNumber={watch("floorsNumber")}
														setFloorsNumber={floorNumber => {
															setValue("floorsNumber", floorNumber);
														}}
													/>
													<button className={`standardButton ${styles.submitBtn}`} onClick={onBuyNow}>
														Continue
														<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path
																d="M12.8335 19.8332L16.3851 16.2815C17.093 15.5737 17.093 14.426 16.3851 13.7182L12.8335 10.1665"
																stroke="#FF7926"
																stroke-width="1.5"
																stroke-linecap="round"
																stroke-linejoin="round"
															/>
														</svg>
													</button>
												</div>
											)}
										</div>
										<div
											className={`${styles.paymentOption} ${isOneTimeSelected ? styles.selected : ""}`}
											onClick={() =>
												setValue("selectedPayment", "One Time Payment", {
													shouldValidate: true,
												})
											}
										>
											<div className={styles.oneTimeDetails}>
												<div className={styles.paymentOptionHeader}>
													One-time
													{isOneTimeSelected && (
														<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path
																fillRule="evenodd"
																clipRule="evenodd"
																d="M12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2ZM16.0805 9.97493C16.3428 9.65434 16.2955 9.18183 15.9749 8.91953C15.6543 8.65724 15.1818 8.70449 14.9195 9.02507L10.9443 13.8837L9.53033 12.4697C9.23744 12.1768 8.76256 12.1768 8.46967 12.4697C8.17678 12.7626 8.17678 13.2374 8.46967 13.5303L10.4697 15.5303C10.6195 15.6802 10.8257 15.7596 11.0374 15.7491C11.249 15.7385 11.4463 15.6389 11.5805 15.4749L16.0805 9.97493Z"
																fill="#FF7926"
															/>
														</svg>
													)}
												</div>
											</div>
											{isOneTimeSelected && (
												<div className={styles.paymentForm}>
													<AddOnForm
														solarProduct={solarProduct}
														selectedAddOns={watch("addOns")}
														setSelectedAddOn={addOns => {
															setValue("addOns", addOns);
														}}
														selectedRoofType={watch("roofType")}
														setSelectedRoofType={roofType => {
															setValue("roofType", roofType);
														}}
														floorsNumber={watch("floorsNumber")}
														setFloorsNumber={floorNumber => {
															setValue("floorsNumber", floorNumber);
														}}
													/>
													<button className={`standardButton ${styles.submitBtn}`} onClick={onBuyNow}>
														Buy Now
														<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
															<path
																d="M12.8335 19.8332L16.3851 16.2815C17.093 15.5737 17.093 14.426 16.3851 13.7182L12.8335 10.1665"
																stroke="#FF7926"
																stroke-width="1.5"
																stroke-linecap="round"
																stroke-linejoin="round"
															/>
														</svg>
													</button>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className={`${styles.info} ${styles.showBelow992}`}>
						<div className={styles.infoItem}>
							<div className={styles.itemIcon}>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M8 4.66892V8L10.1554 10.1554M15.25 8C15.25 12.004 12.004 15.25 8 15.25C3.99594 15.25 0.75 12.004 0.75 8C0.75 3.99594 3.99594 0.75 8 0.75C12.004 0.75 15.25 3.99594 15.25 8Z"
										stroke="black"
										stroke-width="1.5"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</div>
							Up to {solarProduct?.solarProduct.activeDuration} HOURS
						</div>
						<div className={styles.infoItem}>
							<div className={styles.itemIcon}>
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M14.75 4.52273V2.88636C14.75 1.98262 14.0174 1.25 13.1136 1.25H2.88636C1.98262 1.25 1.25 1.98262 1.25 2.88636V4.52273M14.75 4.52273V13.1136C14.75 14.0174 14.0174 14.75 13.1136 14.75H2.88636C1.98262 14.75 1.25 14.0174 1.25 13.1136V4.52273M14.75 4.52273H1.25M4.72727 8V7.99182M8 8V7.99182M4.72727 11.2727V11.2645M8 11.2727V11.2645M11.2727 8V7.99182M4.93182 8C4.93182 8.11299 4.84024 8.20455 4.72727 8.20455C4.61431 8.20455 4.52273 8.11299 4.52273 8C4.52273 7.88701 4.61431 7.79545 4.72727 7.79545C4.84024 7.79545 4.93182 7.88701 4.93182 8ZM8.20455 8C8.20455 8.11299 8.11299 8.20455 8 8.20455C7.88701 8.20455 7.79545 8.11299 7.79545 8C7.79545 7.88701 7.88701 7.79545 8 7.79545C8.11299 7.79545 8.20455 7.88701 8.20455 8ZM4.93182 11.2727C4.93182 11.3857 4.84024 11.4773 4.72727 11.4773C4.61431 11.4773 4.52273 11.3857 4.52273 11.2727C4.52273 11.1597 4.61431 11.0682 4.72727 11.0682C4.84024 11.0682 4.93182 11.1597 4.93182 11.2727ZM8.20455 11.2727C8.20455 11.3857 8.11299 11.4773 8 11.4773C7.88701 11.4773 7.79545 11.3857 7.79545 11.2727C7.79545 11.1597 7.88701 11.0682 8 11.0682C8.11299 11.0682 8.20455 11.1597 8.20455 11.2727ZM11.4773 8C11.4773 8.11299 11.3857 8.20455 11.2727 8.20455C11.1597 8.20455 11.0682 8.11299 11.0682 8C11.0682 7.88701 11.1597 7.79545 11.2727 7.79545C11.3857 7.79545 11.4773 7.88701 11.4773 8Z"
										stroke="black"
										stroke-width="1.5"
										stroke-linecap="round"
									/>
								</svg>
							</div>
							Up to a {solarProduct?.availableOffers.sort((a, b) => b.months - a.months)[0].months} Months Plan
						</div>
						<div className={styles.infoItem}>
							<div className={styles.itemIcon}>
								<svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M4.27713 4.18243H2.60146C1.67601 4.18243 0.925781 4.93266 0.925781 5.85811V10.8851C0.925781 12.736 2.42623 14.2365 4.27713 14.2365H7.00011M4.27713 4.18243V1.25M4.27713 4.18243H9.72308M7.00011 14.2365H9.72308C11.5739 14.2365 13.0744 12.736 13.0744 10.8851V5.85811C13.0744 4.93266 12.3242 4.18243 11.3988 4.18243H9.72308M7.00011 14.2365V16.75M9.72308 4.18243V1.25"
										stroke="black"
										stroke-width="1.5"
										stroke-linecap="round"
										stroke-linejoin="round"
									/>
								</svg>
							</div>
							{solarProduct?.solarProduct.productCapacity} KVA
						</div>
					</div>
					<div className={styles.systemOverviewContainer}>
						<div className={styles.systemOverview}>
							System Overview
							<div className={styles.systemProperties}>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Battery</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.batteryType}</div>
								</div>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Battery Voltage</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.batteryVoltage} V</div>
								</div>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Inverter</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.inverterType}</div>
								</div>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Component Name</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.productName}</div>
								</div>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Component Type</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.type}</div>
								</div>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Energy Capacity</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.productCapacity} KvA</div>
								</div>
								<div className={styles.property}>
									<div className={styles.systemPropertyName}>Warranty</div>
									<div className={styles.systemPropertyValue}>{solarProduct?.solarProduct.warranty}</div>
								</div>
							</div>
						</div>
						<div className={styles.paymentBreakdown}>
							Payment Breakdown
							{selectedPlan ? (
								<div className={styles.paymentProperties}>
									<div className={styles.property}>
										<div className={styles.paymentPropertyName}>{selectedPlan.months}months payments of</div>
										<div className={styles.paymentPropertyValue}>₦{selectedPlan.price.toLocaleString()} / Month</div>
									</div>
									<div className={styles.property}>
										<div className={styles.paymentPropertyName}>Initial deposit of</div>
										<div className={styles.paymentPropertyValue}>₦{(solarProduct?.solarProduct.price! * 0.3).toLocaleString()}</div>
									</div>
									<div className={styles.property}>
										<div className={styles.paymentPropertyName}>Total payment of</div>
										<div className={styles.paymentPropertyValue}>
											₦{(solarProduct?.solarProduct.price! * 0.3 + selectedPlan.months * selectedPlan.price).toLocaleString()}
										</div>
									</div>
								</div>
							) : (
								<span>Select one of the available payment plans</span>
							)}
						</div>
					</div>

					{recommendedProducts && recommendedProducts.length > 0 && (
						<div className={styles.recommendations}>
							You Might Also Like
							<div className={styles.productItems}>
								{recommendedProducts.map((x: SolarProductObject) => (
									<ProductItem onWishlistChange={handleWishlistChange} solarProduct={x} />
								))}
							</div>
						</div>
					)}
				</div>

				{isDialogOpen && <PaymentPlanSideDialog onCloseDialog={closeDialog} solarProduct={solarProduct} handleSelectPlan={handleSelectPlan} />}
			</div>
		</CommonLoadingSpinnerForPage>
	);
};

export default StoreFrontItem;
